// src/components/LegendBadge.tsx
import React from 'react';
import { Trophy, Medal, Star, ShieldCheck } from 'lucide-react';
import { motion } from 'framer-motion';

export type LegendLevel = 'legend' | 'pro' | 'influencer';

interface LegendBadgeProps {
  level: LegendLevel;
  className?: string;
  showText?: boolean;
  animated?: boolean;
}

export const LegendBadge: React.FC<LegendBadgeProps> = ({
  level,
  className = '',
  showText = true,
  animated = true,
}) => {
  const getBadgeConfig = () => {
    switch (level) {
      case 'legend':
        return {
          icon: Trophy,
          color: 'text-amber-400',
          bgColor: 'bg-amber-400/10',
          borderColor: 'border-amber-400/30',
          label: 'Lenda Viva',
          glow: 'shadow-[0_0_15px_rgba(251,191,36,0.3)]',
        };
      case 'pro':
        return {
          icon: Medal,
          color: 'text-slate-300',
          bgColor: 'bg-slate-300/10',
          borderColor: 'border-slate-300/30',
          label: 'Pró',
          glow: 'shadow-[0_0_10px_rgba(203,213,225,0.2)]',
        };
      case 'influencer':
      default:
        return {
          icon: Star,
          color: 'text-blue-400',
          bgColor: 'bg-blue-400/10',
          borderColor: 'border-blue-400/30',
          label: 'Influenciador',
          glow: 'shadow-[0_0_10px_rgba(96,165,250,0.2)]',
        };
    }
  };

  const config = getBadgeConfig();
  const Icon = config.icon;

  const content = (
    <div className={`
      inline-flex items-center gap-1.5 px-3 py-1 rounded-full border backdrop-blur-md
      ${config.bgColor} ${config.borderColor} ${config.glow} ${className}
    `}>
      <Icon className={`${config.color} shrink-0`} size={14} fill="currentColor" fillOpacity={0.2} />
      {showText && (
        <span className={`text-[10px] font-black uppercase tracking-wider ${config.color}`}>
          {config.label}
        </span>
      )}
      <ShieldCheck className="text-white/50" size={12} />
    </div>
  );

  if (animated) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      >
        {content}
      </motion.div>
    );
  }

  return content;
};
